import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

// En producción usa WordPress; REACT_APP_API_BASE permite probar otros entornos sin tocar el código.
const API_BASE = process.env.REACT_APP_API_BASE || 'https://oposicionesage.es/wp-json/oage/v2';

const BLOCK_TOOLS = [
  { type: 'text', icon: '¶', label: 'Texto', description: 'Párrafo normal', keywords: 'parrafo texto normal' },
  { type: 'h1', icon: 'H1', label: 'Título 1', description: 'Título principal', keywords: 'titulo encabezado grande' },
  { type: 'h2', icon: 'H2', label: 'Título 2', description: 'Título de sección', keywords: 'titulo encabezado medio' },
  { type: 'h3', icon: 'H3', label: 'Título 3', description: 'Título pequeño', keywords: 'titulo encabezado pequeno' },
  { type: 'bullet', icon: '•', label: 'Lista', description: 'Lista con viñetas', keywords: 'lista vineta puntos' },
  { type: 'numbered', icon: '1.', label: 'Lista numerada', description: 'Numeración automática', keywords: 'lista numerada numero ordenada' },
  { type: 'todo', icon: '☐', label: 'Tarea', description: 'Elemento para marcar', keywords: 'tarea checkbox casilla' },
  { type: 'callout', icon: '💡', label: 'Destacado', description: 'Aviso o idea importante', keywords: 'destacado aviso nota callout' },
  { type: 'divider', icon: '—', label: 'Separador', description: 'Línea divisoria', keywords: 'linea separador divisor' },
  { type: 'image', icon: '🖼️', label: 'Imagen', description: 'Subir una imagen', keywords: 'imagen foto subir' },
  { type: 'table', icon: '▦', label: 'Tabla', description: 'Filas y columnas', keywords: 'tabla filas columnas' }
];

const TEXT_BLOCK_TYPES = ['text', 'h1', 'h2', 'h3', 'callout', 'bullet', 'numbered', 'todo'];
const AUTOSAVE_DELAY_MS = 10000;

const createBlock = (type = 'text', content = '') => ({
  id: Date.now() * 1000 + Math.floor(Math.random() * 1000),
  type,
  content,
  ...(type === 'table' && { headers: ['Col 1', 'Col 2'], rows: [['', '']] }),
  ...(type === 'todo' && { checked: false }),
  ...(type === 'image' && { url: '', caption: '' })
});

function App() {
  const [token, setToken] = useState(null);
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'editor'
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Auth & Load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appToken = params.get('app_token');
    
    if (appToken) {
      localStorage.setItem('oage_token', appToken);
      setToken(appToken);
      window.history.replaceState({}, '', '/');
    } else {
      const stored = localStorage.getItem('oage_token');
      setToken(stored);
    }
  }, []);

  useEffect(() => {
    if (token) {
      loadPages();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const api = async (endpoint, options = {}) => {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!res.ok) {
      if (res.status === 401) {
        localStorage.removeItem('oage_token');
        setToken(null);
        throw new Error('Sesión expirada');
      }
      throw new Error('Error en la petición');
    }
    
    return res.json();
  };

  const loadPages = async () => {
    try {
      const data = await api('/study-pages');
      setPages(data.pages || []);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const createPage = async () => {
    try {
      const data = await api('/study-pages', {
        method: 'POST',
        body: JSON.stringify({
          title: 'Nueva página',
          icon: '📄',
          color: '#6C63FF',
          content: [{ type: 'text', id: Date.now(), content: '' }]
        })
      });
      
      showToast('Página creada', 'success');
      
      // Load the new page
      const pageData = await api(`/study-pages/${data.id}`);
      setCurrentPage(pageData.page);
      setView('editor');
      await loadPages();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const openPage = async (pageId) => {
    try {
      const data = await api(`/study-pages/${pageId}`);
      setCurrentPage(data.page);
      setView('editor');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const savePage = async (pageData, { silent = false } = {}) => {
    try {
      await api(`/study-pages/${pageData.id}`, {
        method: 'PUT',
        body: JSON.stringify(pageData)
      });
      if (!silent) showToast('Guardado', 'success');
      await loadPages();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('¿Eliminar esta página?')) return;
    
    try {
      await api(`/study-pages/${pageId}`, { method: 'DELETE' });
      showToast('Página eliminada', 'success');
      await loadPages();
      if (currentPage?.id === pageId) {
        setView('dashboard');
        setCurrentPage(null);
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const updateStatus = async (pageId, status) => {
    try {
      await api(`/study-pages/${pageId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      await loadPages();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const markReviewed = async (pageId) => {
    try {
      await api(`/study-pages/${pageId}/review`, { method: 'POST' });
      showToast('¡Repasado!', 'success');
      await loadPages();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const generatePDF = async (pageId) => {
    try {
      showToast('Generando PDF...', 'info');
      const data = await api(`/study-pages/${pageId}/pdf`, { method: 'POST' });
      
      if (data.is_html) {
        window.open(data.url, '_blank');
        showToast('Usa Ctrl+P para imprimir', 'info');
      } else {
        window.location.href = data.url;
        showToast('PDF generado', 'success');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  if (!token) {
    return (
      <div className="unauthorized">
        <h1>🔒 No autorizado</h1>
        <p>Debes acceder desde OposicionesAGE.com</p>
        <a href="https://oposicionesage.com" className="btn-primary">Volver</a>
      </div>
    );
  }

  return (
    <div className="app">
      {toast && <Toast message={toast.message} type={toast.type} />}
      
      {view === 'dashboard' ? (
        <Dashboard 
          pages={pages}
          onCreate={createPage}
          onOpen={openPage}
          onDelete={deletePage}
          onUpdateStatus={updateStatus}
          onMarkReviewed={markReviewed}
        />
      ) : (
        <Editor 
          page={currentPage}
          onSave={savePage}
          onBack={() => setView('dashboard')}
          onDelete={deletePage}
          onGeneratePDF={generatePDF}
          token={token}
        />
      )}
    </div>
  );
}

// ========== DASHBOARD ==========
function Dashboard({ pages, onCreate, onOpen, onDelete, onUpdateStatus, onMarkReviewed }) {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredPages = pages.filter(p => 
    p.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const columns = {
    inicio: filteredPages.filter(p => p.status === 'inicio'),
    consolidando: filteredPages.filter(p => p.status === 'consolidando'),
    consolidado: filteredPages.filter(p => p.status === 'consolidado')
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>📚 Study Pages</h1>
        <div className="header-actions">
          <input 
            type="search"
            placeholder="Buscar páginas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button onClick={onCreate} className="btn-primary">
            ➕ Nueva página
          </button>
        </div>
      </header>

      <div className="kanban">
        <KanbanColumn 
          title="🟣 Inicio"
          pages={columns.inicio}
          status="inicio"
          onOpen={onOpen}
          onDelete={onDelete}
          onUpdateStatus={onUpdateStatus}
          onMarkReviewed={onMarkReviewed}
        />
        <KanbanColumn 
          title="🟠 Consolidando"
          pages={columns.consolidando}
          status="consolidando"
          onOpen={onOpen}
          onDelete={onDelete}
          onUpdateStatus={onUpdateStatus}
          onMarkReviewed={onMarkReviewed}
        />
        <KanbanColumn 
          title="🟢 Consolidado"
          pages={columns.consolidado}
          status="consolidado"
          onOpen={onOpen}
          onDelete={onDelete}
          onUpdateStatus={onUpdateStatus}
          onMarkReviewed={onMarkReviewed}
        />
      </div>
    </div>
  );
}

function KanbanColumn({ title, pages, status, onOpen, onDelete, onUpdateStatus, onMarkReviewed }) {
  const statuses = ['inicio', 'consolidando', 'consolidado'];
  
  return (
    <div className="kanban-column">
      <div className="column-header">
        <h2>{title}</h2>
        <span className="count">{pages.length}</span>
      </div>
      <div className="column-cards">
        {pages.map(page => (
          <div key={page.id} className="card" onClick={() => onOpen(page.id)}>
            <div className="card-header">
              <span className="card-icon" style={{ color: page.color }}>{page.icon}</span>
              <h3>{page.title}</h3>
            </div>
            
            {page.needs_review && (
              <div className="card-badge badge-warning">⚠️ ¡Repasa!</div>
            )}
            
            <div className="card-meta">
              <small>{new Date(page.updated_at).toLocaleDateString('es-ES')}</small>
            </div>
            
            <div className="card-actions" onClick={(e) => e.stopPropagation()}>
              {page.needs_review && (
                <button 
                  className="card-action-btn"
                  onClick={() => onMarkReviewed(page.id)}
                  title="Marcar como repasado"
                >
                  ✓ Repaso
                </button>
              )}
              
              {statuses.map(st => st !== status && (
                <button
                  key={st}
                  className="card-action-btn"
                  onClick={() => onUpdateStatus(page.id, st)}
                  title={`Mover a ${st}`}
                >
                  {st === 'inicio' && '🟣'}
                  {st === 'consolidando' && '🟠'}
                  {st === 'consolidado' && '🟢'}
                </button>
              ))}
              
              <button 
                className="card-action-btn delete"
                onClick={() => onDelete(page.id)}
                title="Eliminar"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
        
        {pages.length === 0 && (
          <div className="empty-column">Sin páginas</div>
        )}
      </div>
    </div>
  );
}

// ========== EDITOR ==========
function Editor({ page, onSave, onBack, onGeneratePDF, token }) {
  const [blocks, setBlocks] = useState([]);
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState('📄');
  const [color, setColor] = useState('#6C63FF');
  const [isDirty, setIsDirty] = useState(false);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [pendingFocus, setPendingFocus] = useState(null);
  const [slashMenu, setSlashMenu] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const titleRef = useRef(null);

  useEffect(() => {
    if (!page) return;
    const initialBlocks = page.content?.length ? page.content : [createBlock()];
    setTitle(page.title || '');
    setIcon(page.icon || '📄');
    setColor(page.color || '#6C63FF');
    setBlocks(initialBlocks);
    setActiveBlockId(initialBlocks[0].id);
    setIsDirty(false);
  }, [page]);

  const handleSave = useCallback((silent = false) => {
    if (!page) return;
    onSave({ id: page.id, title, icon, color, content: blocks }, { silent });
    setIsDirty(false);
  }, [page, title, icon, color, blocks, onSave]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const timer = setTimeout(() => handleSave(true), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isDirty, handleSave]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [handleSave]);

  useEffect(() => {
    if (!pendingFocus) return;
    const frame = requestAnimationFrame(() => {
      const wrapper = document.querySelector(`[data-block-id="${pendingFocus.id}"]`);
      const input = wrapper?.querySelector('[data-block-input="true"]');
      if (input) {
        input.focus();
        const position = pendingFocus.position === 'start' ? 0 : input.value.length;
        input.setSelectionRange?.(position, position);
      }
      setPendingFocus(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingFocus, blocks]);

  const updateBlock = (id, updates) => {
    setBlocks(current => current.map(block => block.id === id ? { ...block, ...updates } : block));
    setIsDirty(true);
  };

  const addBlock = (type = 'text', afterIndex = -1, content = '') => {
    const newBlock = createBlock(type, content);
    const activeIndex = blocks.findIndex(block => block.id === activeBlockId);
    const insertionIndex = afterIndex >= 0 ? afterIndex + 1 : activeIndex >= 0 ? activeIndex + 1 : blocks.length;
    const nextBlocks = [...blocks];
    nextBlocks.splice(insertionIndex, 0, newBlock);
    setBlocks(nextBlocks);
    setActiveBlockId(newBlock.id);
    setPendingFocus({ id: newBlock.id, position: 'start' });
    setSlashMenu(null);
    setIsDirty(true);
    return newBlock;
  };

  const convertBlock = (id, type, content = '') => {
    const defaults = createBlock(type, content);
    setSlashMenu(null);
    setIsDirty(true);

    if (type === 'divider') {
      const blockIndex = blocks.findIndex(block => block.id === id);
      const followingBlock = createBlock('text');
      const nextBlocks = blocks.map(block => block.id === id ? { ...defaults, id } : block);
      nextBlocks.splice(blockIndex + 1, 0, followingBlock);
      setBlocks(nextBlocks);
      setActiveBlockId(followingBlock.id);
      setPendingFocus({ id: followingBlock.id, position: 'start' });
    } else if (TEXT_BLOCK_TYPES.includes(type)) {
      setBlocks(current => current.map(block => block.id === id ? { ...defaults, id } : block));
      setActiveBlockId(id);
      setPendingFocus({ id, position: 'end' });
    } else {
      setBlocks(current => current.map(block => block.id === id ? { ...defaults, id } : block));
      setActiveBlockId(id);
    }
  };

  const deleteBlock = (id, focusPrevious = false) => {
    const blockIndex = blocks.findIndex(block => block.id === id);
    if (blocks.length === 1) {
      convertBlock(id, 'text');
      return;
    }
    const nextBlocks = blocks.filter(block => block.id !== id);
    setBlocks(nextBlocks);
    setSlashMenu(null);
    setIsDirty(true);
    if (focusPrevious) {
      const target = nextBlocks[Math.max(0, blockIndex - 1)];
      if (target) {
        setActiveBlockId(target.id);
        setPendingFocus({ id: target.id, position: 'end' });
      }
    }
  };

  const requestBlockDeletion = (block, focusPrevious = true) => {
    setSlashMenu(null);
    setDeleteConfirmation({
      id: block.id,
      type: block.type,
      content: block.content || '',
      focusPrevious
    });
  };

  const confirmBlockDeletion = () => {
    if (!deleteConfirmation) return;
    const { id, focusPrevious } = deleteConfirmation;
    setDeleteConfirmation(null);
    deleteBlock(id, focusPrevious);
  };

  const moveBlock = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= blocks.length) return;
    const nextBlocks = [...blocks];
    const [moved] = nextBlocks.splice(fromIndex, 1);
    nextBlocks.splice(toIndex, 0, moved);
    setBlocks(nextBlocks);
    setIsDirty(true);
    setPendingFocus({ id: moved.id, position: 'end' });
  };

  const splitBlock = (block, index, selectionStart, selectionEnd) => {
    const content = block.content || '';
    const beforeCursor = content.slice(0, selectionStart);
    const afterCursor = content.slice(selectionEnd);
    const isList = ['bullet', 'numbered', 'todo'].includes(block.type);

    if (isList && !content.trim()) {
      convertBlock(block.id, 'text');
      return;
    }

    const nextType = isList ? block.type : 'text';
    const followingBlock = createBlock(nextType, afterCursor);
    const nextBlocks = [...blocks];
    nextBlocks[index] = { ...block, content: beforeCursor };
    nextBlocks.splice(index + 1, 0, followingBlock);
    setBlocks(nextBlocks);
    setActiveBlockId(followingBlock.id);
    setPendingFocus({ id: followingBlock.id, position: 'start' });
    setSlashMenu(null);
    setIsDirty(true);
  };

  const handleEmptyBackspace = (block) => {
    if (block.type !== 'text') {
      convertBlock(block.id, 'text');
      return;
    }
    requestBlockDeletion(block, true);
  };

  const focusSibling = (index, direction) => {
    const target = blocks[index + direction];
    if (!target) return;
    setActiveBlockId(target.id);
    setPendingFocus({ id: target.id, position: direction < 0 ? 'end' : 'start' });
  };

  const normalizedQuery = (slashMenu?.query || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const slashCommands = BLOCK_TOOLS.filter(tool =>
    `${tool.label} ${tool.keywords}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().includes(normalizedQuery)
  );
  const selectedCommand = Math.min(slashMenu?.selectedIndex || 0, Math.max(0, slashCommands.length - 1));

  const openSlashMenu = (blockId, query) => setSlashMenu({ blockId, query, selectedIndex: 0 });
  const moveSlashSelection = (direction) => {
    if (!slashCommands.length) return;
    setSlashMenu(current => ({
      ...current,
      selectedIndex: (current.selectedIndex + direction + slashCommands.length) % slashCommands.length
    }));
  };
  const chooseSlashCommand = (blockId, tool = slashCommands[selectedCommand]) => {
    if (tool) convertBlock(blockId, tool.type);
  };

  const handleBack = () => {
    if (isDirty) handleSave(true);
    onBack();
  };

  return (
    <div className="editor">
      <div className="editor-topbar">
        <button onClick={handleBack} className="btn-ghost">← Volver</button>
        <span className={`save-status ${isDirty ? 'save-status-dirty' : ''}`} aria-live="polite">
          {isDirty ? 'Cambios sin guardar' : 'Guardado'}
        </span>
        <div className="editor-actions">
          <button onClick={() => onGeneratePDF(page.id)} className="btn-ghost">📥 PDF</button>
          <label className="color-picker">
            <span>Color</span>
            <input
              type="color"
              value={color}
              onChange={(event) => { setColor(event.target.value); setIsDirty(true); }}
            />
          </label>
          <button onClick={() => handleSave()} className="btn-primary" title="Guardar (Ctrl+S)">
            💾 Guardar
          </button>
        </div>
      </div>

      <main className="editor-content">
        <div className="page-header">
          <input
            type="text"
            className="icon-input"
            aria-label="Icono de la página"
            value={icon}
            onChange={(event) => { setIcon(event.target.value); setIsDirty(true); }}
            maxLength="2"
          />
          <input
            ref={titleRef}
            type="text"
            className="title-input"
            placeholder="Título de la página"
            value={title}
            onChange={(event) => { setTitle(event.target.value); setIsDirty(true); }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const firstBlock = blocks[0];
                if (firstBlock) setPendingFocus({ id: firstBlock.id, position: 'start' });
              }
            }}
          />
        </div>

        <BlockToolbar onAddBlock={(type) => addBlock(type)} />

        <div className="keyboard-hint">
          Escribe <kbd>/</kbd> para insertar · <kbd>Enter</kbd> continúa · <kbd>Ctrl</kbd> + <kbd>S</kbd> guarda
        </div>

        <div className="blocks">
          {blocks.map((block, index) => (
            <Block
              key={block.id}
              block={block}
              index={index}
              isLast={index === blocks.length - 1}
              isActive={block.id === activeBlockId}
              onUpdate={(updates) => updateBlock(block.id, updates)}
              onConvert={(type, content = '') => convertBlock(block.id, type, content)}
              onDelete={() => requestBlockDeletion(block, true)}
              onMoveUp={() => moveBlock(index, index - 1)}
              onMoveDown={() => moveBlock(index, index + 1)}
              onActivate={() => setActiveBlockId(block.id)}
              onEnter={(start, end) => splitBlock(block, index, start, end)}
              onEmptyBackspace={() => handleEmptyBackspace(block)}
              onFocusPrevious={() => focusSibling(index, -1)}
              onFocusNext={() => focusSibling(index, 1)}
              slashMenu={slashMenu?.blockId === block.id ? slashMenu : null}
              slashCommands={slashCommands}
              selectedCommand={selectedCommand}
              onSlashChange={(query) => openSlashMenu(block.id, query)}
              onSlashClose={() => setSlashMenu(null)}
              onSlashMove={moveSlashSelection}
              onSlashSelect={(tool) => chooseSlashCommand(block.id, tool)}
              token={token}
              allBlocks={blocks}
            />
          ))}
        </div>
      </main>

      {deleteConfirmation && (
        <DeleteConfirmationDialog
          block={deleteConfirmation}
          onCancel={() => setDeleteConfirmation(null)}
          onConfirm={confirmBlockDeletion}
        />
      )}
    </div>
  );
}

function DeleteConfirmationDialog({ block, onCancel, onConfirm }) {
  const cancelButtonRef = useRef(null);
  const blockLabel = BLOCK_TOOLS.find(tool => tool.type === block.type)?.label || 'bloque';
  const preview = block.content.trim();

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    cancelButtonRef.current?.focus();
    const handleEscape = (event) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
      previouslyFocused?.focus?.();
    };
  }, [onCancel]);

  return (
    <div className="delete-confirmation-backdrop" onMouseDown={onCancel}>
      <div
        className="delete-confirmation-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-confirmation-title"
        aria-describedby="delete-confirmation-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="delete-confirmation-icon" aria-hidden="true">!</div>
        <h2 id="delete-confirmation-title">¿Eliminar este bloque?</h2>
        <p id="delete-confirmation-description">
          Se eliminará el componente <strong>{blockLabel}</strong>{preview ? ' y todo su contenido' : ''}.
          Esta acción no se puede deshacer.
        </p>
        {preview && <blockquote>{preview.slice(0, 140)}{preview.length > 140 ? '…' : ''}</blockquote>}
        <div className="delete-confirmation-actions">
          <button ref={cancelButtonRef} type="button" className="btn-ghost delete-confirm-cancel" onClick={onCancel}>
            Cancelar
          </button>
          <button type="button" className="delete-confirm-submit" onClick={onConfirm}>
            Eliminar bloque
          </button>
        </div>
      </div>
    </div>
  );
}

function BlockToolbar({ onAddBlock }) {
  return (
    <div className="block-toolbar" aria-label="Tipos de bloque">
      <span className="toolbar-label">Insertar:</span>
      {BLOCK_TOOLS.map(tool => (
        <button
          key={tool.type}
          className="tool-btn"
          onClick={() => onAddBlock(tool.type)}
          title={tool.label}
          aria-label={`Insertar ${tool.label}`}
        >
          {tool.icon}
        </button>
      ))}
      <span className="toolbar-slash-hint"><kbd>/</kbd> sin ratón</span>
    </div>
  );
}

function SlashMenu({ commands, selectedIndex, onSelect }) {
  return (
    <div className="slash-menu" role="listbox" aria-label="Insertar bloque">
      <div className="slash-menu-title">Insertar bloque</div>
      {commands.length ? commands.map((tool, index) => (
        <button
          key={tool.type}
          type="button"
          role="option"
          aria-selected={index === selectedIndex}
          className={`slash-command ${index === selectedIndex ? 'slash-command-selected' : ''}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelect(tool)}
        >
          <span className="slash-command-icon">{tool.icon}</span>
          <span>
            <strong>{tool.label}</strong>
            <small>{tool.description}</small>
          </span>
        </button>
      )) : (
        <div className="slash-empty">No hay comandos que coincidan</div>
      )}
    </div>
  );
}

function Block({
  block,
  index,
  isLast,
  isActive,
  onUpdate,
  onConvert,
  onDelete,
  onMoveUp,
  onMoveDown,
  onActivate,
  onEnter,
  onEmptyBackspace,
  onFocusPrevious,
  onFocusNext,
  slashMenu,
  slashCommands,
  selectedCommand,
  onSlashChange,
  onSlashClose,
  onSlashMove,
  onSlashSelect,
  token,
  allBlocks
}) {
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch(`${API_BASE}/study-pages/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      if (data.success) {
        onUpdate({ url: data.url });
      }
    } catch (err) {
      alert('Error al subir imagen');
    }
  };
  
  const resizeTextarea = (textarea) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handleTextChange = (event) => {
    const value = event.target.value;
    const markdownShortcuts = {
      '# ': 'h1',
      '## ': 'h2',
      '### ': 'h3',
      '- ': 'bullet',
      '* ': 'bullet',
      '1. ': 'numbered',
      '[] ': 'todo',
      '[ ] ': 'todo'
    };

    if (block.type === 'text' && markdownShortcuts[value]) {
      onConvert(markdownShortcuts[value]);
      return;
    }

    onUpdate({ content: value });
    resizeTextarea(event.currentTarget);
    if (value.startsWith('/') && !value.includes('\n')) {
      onSlashChange(value.slice(1));
    } else if (slashMenu) {
      onSlashClose();
    }
  };

  const handleTextKeyDown = (event) => {
    if (event.nativeEvent?.isComposing) return;

    if (slashMenu) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        onSlashMove(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      if (event.key === 'Enter' && slashCommands.length) {
        event.preventDefault();
        onSlashSelect(slashCommands[selectedCommand]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        onSlashClose();
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onEnter(event.currentTarget.selectionStart, event.currentTarget.selectionEnd);
      return;
    }

    if (event.key === 'Backspace' && !block.content && event.currentTarget.selectionStart === 0) {
      event.preventDefault();
      onEmptyBackspace();
      return;
    }

    if (event.key === 'ArrowUp' && event.currentTarget.selectionStart === 0) {
      event.preventDefault();
      onFocusPrevious();
    } else if (event.key === 'ArrowDown' && event.currentTarget.selectionEnd === block.content.length) {
      event.preventDefault();
      onFocusNext();
    }
  };

  const textArea = (className, placeholder) => (
    <textarea
      data-block-input="true"
      className={className}
      placeholder={placeholder}
      value={block.content || ''}
      onChange={handleTextChange}
      onKeyDown={handleTextKeyDown}
      onFocus={onActivate}
      rows="1"
      ref={(textarea) => resizeTextarea(textarea)}
    />
  );

  // Las listas consecutivas forman una secuencia; un bloque de otro tipo la reinicia.
  const getNumberedValue = () => {
    if (block.type !== 'numbered') return 1;
    let value = 1;
    for (let previous = index - 1; previous >= 0; previous--) {
      if (allBlocks[previous].type !== 'numbered') break;
      value++;
    }
    return value;
  };

  return (
    <div
      data-block-id={block.id}
      className={`block ${isActive ? 'block-active' : ''}`}
      onClick={onActivate}
    >
      <div className="block-controls">
        <button type="button" className="block-btn" onClick={onMoveUp} disabled={index === 0} title="Subir bloque">↑</button>
        <button type="button" className="block-btn" onClick={onMoveDown} disabled={isLast} title="Bajar bloque">↓</button>
        <button type="button" className="block-btn delete" onClick={onDelete} title="Eliminar bloque">×</button>
      </div>

      <div className="block-content">
        {block.type === 'h1' && textArea('input-h1', 'Título 1')}
        {block.type === 'h2' && textArea('input-h2', 'Título 2')}
        {block.type === 'h3' && textArea('input-h3', 'Título 3')}
        {block.type === 'text' && textArea('input-text', "Escribe '/' para insertar un bloque")}
        {block.type === 'callout' && textArea('input-callout', '💡 Nota importante')}
        {block.type === 'bullet' && textArea('input-bullet', 'Elemento de lista')}

        {block.type === 'numbered' && (
          <div className="numbered-block">
            <span className="numbered-marker">{getNumberedValue()}.</span>
            {textArea('input-numbered', 'Elemento numerado')}
          </div>
        )}

        {block.type === 'todo' && (
          <div className="todo-block">
            <input
              type="checkbox"
              checked={block.checked || false}
              onChange={(event) => onUpdate({ checked: event.target.checked })}
            />
            {textArea('input-todo', 'Tarea...')}
          </div>
        )}

        {block.type === 'divider' && <hr className="divider" />}

        {block.type === 'image' && (
          <div className="image-block">
            {block.url ? (
              <>
                <img src={block.url} alt="" />
                <input 
                  type="text"
                  className="image-caption"
                  placeholder="Pie de foto..."
                  value={block.caption || ''}
                  onChange={(e) => onUpdate({ caption: e.target.value })}
                />
              </>
            ) : (
              <div className="image-upload">
                <input 
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  id={`img-${block.id}`}
                  style={{ display: 'none' }}
                />
                <label htmlFor={`img-${block.id}`} className="btn-primary">
                  Seleccionar imagen
                </label>
              </div>
            )}
          </div>
        )}
        
        {block.type === 'table' && (
          <TableBlock block={block} onUpdate={onUpdate} />
        )}

        {slashMenu && (
          <SlashMenu
            commands={slashCommands}
            selectedIndex={selectedCommand}
            onSelect={onSlashSelect}
          />
        )}
      </div>
    </div>
  );
}

function TableBlock({ block, onUpdate }) {
  const addColumn = () => {
    const headers = [...block.headers, `Col ${block.headers.length + 1}`];
    const rows = block.rows.map(row => [...row, '']);
    onUpdate({ headers, rows });
  };

  const addRow = () => {
    const rows = [...block.rows, Array(block.headers.length).fill('')];
    onUpdate({ rows });
  };

  const deleteColumn = (colIndex) => {
    if (block.headers.length <= 1) {
      alert('Debe haber al menos una columna');
      return;
    }
    const headers = block.headers.filter((_, i) => i !== colIndex);
    const rows = block.rows.map(row => row.filter((_, i) => i !== colIndex));
    onUpdate({ headers, rows });
  };

  const deleteRow = (rowIndex) => {
    if (block.rows.length <= 1) {
      alert('Debe haber al menos una fila');
      return;
    }
    const rows = block.rows.filter((_, i) => i !== rowIndex);
    onUpdate({ rows });
  };

  const updateHeader = (index, value) => {
    const headers = [...block.headers];
    headers[index] = value;
    onUpdate({ headers });
  };

  const updateCell = (rowIndex, colIndex, value) => {
    const rows = [...block.rows];
    rows[rowIndex][colIndex] = value;
    onUpdate({ rows });
  };

  return (
    <div className="table-block">
      <table>
        <thead>
          <tr>
            {block.headers.map((h, i) => (
              <th key={i}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input 
                    value={h}
                    onChange={(e) => updateHeader(i, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button 
                    onClick={() => deleteColumn(i)}
                    className="table-delete-btn"
                    title="Eliminar columna"
                  >
                    ×
                  </button>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci}>
                  <input 
                    value={cell}
                    onChange={(e) => updateCell(ri, ci, e.target.value)}
                  />
                </td>
              ))}
              <td className="table-row-actions">
                <button 
                  onClick={() => deleteRow(ri)}
                  className="table-delete-btn"
                  title="Eliminar fila"
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="table-actions">
        <button onClick={addColumn} className="btn-ghost">+ Columna</button>
        <button onClick={addRow} className="btn-ghost">+ Fila</button>
      </div>
    </div>
  );
}

// ========== TOAST ==========
function Toast({ message, type }) {
  const colors = {
    success: '#10b981',
    error: '#ef4444',
    info: '#3b82f6'
  };

  return (
    <div className="toast" style={{ background: colors[type] || colors.info }}>
      {message}
    </div>
  );
}

export default App;
